import { useState, useEffect, useCallback } from 'react';
import { useRepliconNavigationContext } from 'modules/common/hooks/RepliconNavigationProvider';
import { delay } from 'utils/delay';

const ONLINE_POLLING_INTERVAL = 15 * 1000;
const OFFLINE_POLLING_INTERVAL = 2 * 1000;

async function checkOnline(
  retry = 1,
  navigationMetadata: any
): Promise<boolean> {
  if (!window.navigator.onLine) {
    return false;
  }

  // avoid CORS errors with a request to your own origin
  const url = new URL(window.location.origin);

  // random value to prevent cached responses
  url.searchParams.set('rand', Math.random().toString(36).substring(2, 15));

  try {
    const response = await fetch(url.toString(), { method: 'HEAD' });
    return response.ok;
  } catch (err: any) {
    if (err) {
      if (!navigationMetadata?.isEditIntegrationPermission) {
        window.location.href = navigationMetadata?.redirection403Url;
      }
      window.location.href = navigationMetadata?.sessionTimeoutRedirectUrl;
    }
    if (retry > 0) {
      retry--;
      await delay(1000);
      return checkOnline(retry, navigationMetadata);
    }
    return false;
  }
}

export const useOfflineState = (
  onlinePollingInterval = ONLINE_POLLING_INTERVAL,
  offlinePollingInterval = OFFLINE_POLLING_INTERVAL
): any => {
  const [isOnline, setIsOnline] = useState(true);
  const [pollingInterval, setPollingInterval] = useState(onlinePollingInterval);
  const navigationMetadata = useRepliconNavigationContext();

  const updateOnlineStatus = useCallback(async () => {
    const isOnline = await checkOnline(1, navigationMetadata);

    setIsOnline(isOnline);
    setPollingInterval(
      isOnline ? onlinePollingInterval : offlinePollingInterval
    );
  }, [offlinePollingInterval, onlinePollingInterval, navigationMetadata]);

  useEffect(() => {
    let interval: any = undefined;

    interval = setInterval(updateOnlineStatus, pollingInterval);

    window.addEventListener('offline', updateOnlineStatus);
    window.addEventListener('online', updateOnlineStatus);

    return () => {
      clearInterval(interval);
      window.removeEventListener('offline', updateOnlineStatus);
      window.removeEventListener('online', updateOnlineStatus);
    };
  }, [pollingInterval, updateOnlineStatus]);

  return isOnline;
};
